/**
 * G-P0-09 — minimal IndexedDB outbox for failed customer analytics emits.
 */
import { shouldDiscardAnalyticsOutboxError } from 'pi-kiosk-shared/analyticsIngestOutboxPolicy';
import type { CustomerAnalyticsClient, CustomerEmitEventInput } from './customerAnalyticsClient.js';

export const CUSTOMER_ANALYTICS_OUTBOX_DB_NAME = 'rpapp-customer-analytics-outbox';
export const CUSTOMER_ANALYTICS_OUTBOX_STORE = 'events';
export const CUSTOMER_ANALYTICS_OUTBOX_DB_VERSION = 1;
export const CUSTOMER_ANALYTICS_OUTBOX_DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export const CUSTOMER_ANALYTICS_OUTBOX_MAX_SIZE = 500;

export interface CustomerAnalyticsOutboxRecord {
  readonly id: string;
  readonly sessionId: string;
  readonly payload: CustomerEmitEventInput;
  readonly queuedAtMs: number;
}

export interface CustomerAnalyticsOutboxStorage {
  open(): Promise<void>;
  enqueue(record: CustomerAnalyticsOutboxRecord): Promise<void>;
  list(): Promise<readonly CustomerAnalyticsOutboxRecord[]>;
  remove(id: string): Promise<void>;
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function createInMemoryCustomerAnalyticsOutboxStorage(): CustomerAnalyticsOutboxStorage & {
  resetForTests: () => void;
} {
  const records: CustomerAnalyticsOutboxRecord[] = [];
  return {
    async open(): Promise<void> {
      return;
    },
    async enqueue(record: CustomerAnalyticsOutboxRecord): Promise<void> {
      records.push(record);
      if (records.length > CUSTOMER_ANALYTICS_OUTBOX_MAX_SIZE) {
        records.shift();
      }
    },
    async list(): Promise<readonly CustomerAnalyticsOutboxRecord[]> {
      return [...records];
    },
    async remove(id: string): Promise<void> {
      const idx = records.findIndex((row) => row.id === id);
      if (idx >= 0) {
        records.splice(idx, 1);
      }
    },
    resetForTests(): void {
      records.length = 0;
    },
  };
}

export function createIndexedDbCustomerAnalyticsOutboxStorage(): CustomerAnalyticsOutboxStorage {
  let dbPromise: Promise<IDBDatabase> | null = null;

  const openDb = async (): Promise<IDBDatabase> => {
    if (dbPromise) {
      return dbPromise;
    }
    if (typeof indexedDB === 'undefined') {
      throw new Error('indexeddb_unavailable');
    }
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(
        CUSTOMER_ANALYTICS_OUTBOX_DB_NAME,
        CUSTOMER_ANALYTICS_OUTBOX_DB_VERSION,
      );
      request.onupgradeneeded = (): void => {
        const db = request.result;
        if (!db.objectStoreNames.contains(CUSTOMER_ANALYTICS_OUTBOX_STORE)) {
          db.createObjectStore(CUSTOMER_ANALYTICS_OUTBOX_STORE, { keyPath: 'id' });
        }
      };
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => reject(request.error ?? new Error('idb_open_failed'));
    });
    return dbPromise;
  };

  const withStore = async <T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await openDb();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(CUSTOMER_ANALYTICS_OUTBOX_STORE, mode);
      const store = tx.objectStore(CUSTOMER_ANALYTICS_OUTBOX_STORE);
      const request = run(store);
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => reject(request.error ?? new Error('idb_request_failed'));
    });
  };

  return {
    open: async (): Promise<void> => {
      await openDb();
    },
    async enqueue(record: CustomerAnalyticsOutboxRecord): Promise<void> {
      await withStore('readwrite', (store) => store.put(record));
      const all = await this.list();
      if (all.length > CUSTOMER_ANALYTICS_OUTBOX_MAX_SIZE) {
        const overflow = all.length - CUSTOMER_ANALYTICS_OUTBOX_MAX_SIZE;
        const sorted = [...all].sort((a, b) => a.queuedAtMs - b.queuedAtMs);
        for (let i = 0; i < overflow; i += 1) {
          const row = sorted[i];
          if (row) {
            await this.remove(row.id);
          }
        }
      }
    },
    async list(): Promise<readonly CustomerAnalyticsOutboxRecord[]> {
      return withStore('readonly', (store) => store.getAll());
    },
    async remove(id: string): Promise<void> {
      await withStore('readwrite', (store) => store.delete(id));
    },
  };
}

export interface EnqueueCustomerAnalyticsOutboxInput {
  readonly storage: CustomerAnalyticsOutboxStorage;
  readonly sessionId: string;
  readonly payload: CustomerEmitEventInput;
  readonly nowMs?: number;
}

export async function enqueueCustomerAnalyticsOutboxEvent(
  input: EnqueueCustomerAnalyticsOutboxInput,
): Promise<void> {
  await input.storage.open();
  await input.storage.enqueue({
    id: uuidv4(),
    sessionId: input.sessionId,
    payload: input.payload,
    queuedAtMs: input.nowMs ?? Date.now(),
  });
}

export interface PruneCustomerAnalyticsOutboxInput {
  readonly storage: CustomerAnalyticsOutboxStorage;
  readonly nowMs?: number;
  readonly ttlMs?: number;
}

export async function pruneExpiredCustomerAnalyticsOutboxEvents(
  input: PruneCustomerAnalyticsOutboxInput,
): Promise<number> {
  const nowMs = input.nowMs ?? Date.now();
  const ttlMs = input.ttlMs ?? CUSTOMER_ANALYTICS_OUTBOX_DEFAULT_TTL_MS;
  await input.storage.open();
  const rows = await input.storage.list();
  let removed = 0;
  for (const row of rows) {
    if (nowMs - row.queuedAtMs > ttlMs) {
      await input.storage.remove(row.id);
      removed += 1;
    }
  }
  return removed;
}

export interface FlushCustomerAnalyticsOutboxInput {
  readonly storage: CustomerAnalyticsOutboxStorage;
  readonly client: CustomerAnalyticsClient;
  readonly nowMs?: number;
  readonly ttlMs?: number;
}

export async function flushCustomerAnalyticsOutbox(
  input: FlushCustomerAnalyticsOutboxInput,
): Promise<{ flushed: number; remaining: number }> {
  await input.storage.open();
  await pruneExpiredCustomerAnalyticsOutboxEvents({
    storage: input.storage,
    nowMs: input.nowMs,
    ttlMs: input.ttlMs,
  });
  const rows = [...(await input.storage.list())].sort(
    (a, b) => a.queuedAtMs - b.queuedAtMs,
  );
  let flushed = 0;
  for (const row of rows) {
    try {
      await input.client.emit(row.sessionId, row.payload);
      await input.storage.remove(row.id);
      flushed += 1;
    } catch (err) {
      if (shouldDiscardAnalyticsOutboxError(err)) {
        await input.storage.remove(row.id);
        continue;
      }
      break;
    }
  }
  const remaining = (await input.storage.list()).length;
  return { flushed, remaining };
}

export function resolveDefaultCustomerAnalyticsOutboxStorage(): CustomerAnalyticsOutboxStorage {
  if (typeof indexedDB === 'undefined') {
    return createInMemoryCustomerAnalyticsOutboxStorage();
  }
  return createIndexedDbCustomerAnalyticsOutboxStorage();
}
